import React from 'react';
import { useTranslations } from '@unisporkal/localization';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
  CheckRoundedIcon,
  List,
  ListItem,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { ExpandableBuyCard } from '../../../molecules/ExpandableBuyCard';
import { CaretText } from '../../../molecules/CaretText';
import useAcquisitionModal from '../../../../hooks/useAcquisitionModal';
import {
  trackClickViewLicenseDetailsCreativeSubs,
  trackClickViewLicenseDetailsEditorialSubs,
} from '../../../../support/ctaTracking';
import {
  CREATIVE_LICENSE_DETAILS_MODAL_ID,
  STANDARD_EDITORIAL_USAGE_MODAL_ID,
} from '../../../../constants/modalConstants';
import { type AcquisitionModalState } from '../../../../types/acquisitionModalState';
import styles from './SubscriptionBuyCard.module.scss';

export type SubscriptionTier = 'creative' | 'editorial';

export type SubscriptionBuyCardProps = {
  tier: SubscriptionTier;
  pricePerImage: string;
  total: string;
  showSavingsBadge?: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onExpand: () => void;
  onSelect: () => void;
};

// The card has no download-count prop yet; the only whitelisted title keys
// are the "_10_month" variants, so the count is fixed at 10 for both tiers
// until a count prop is introduced.
const MONTHLY_IMAGE_COUNT = 10;

const TITLE_TRANSLATION_KEY: Record<SubscriptionTier, string> = {
  creative: 'num_creative_images_10_month',
  editorial: 'num_editorial_images_10_month',
};

const SUBTITLE_TRANSLATION_KEY: Record<SubscriptionTier, string> = {
  creative: 'annual_plan_single_user',
  editorial: 'annual_plan_editorial_license_single_user',
};

const BENEFIT_TRANSLATION_KEYS: Record<SubscriptionTier, string[]> = {
  creative: [
    'millions_authentic_images_global_network',
    'commercial_ready_content_worldwide_usage_rights',
    'automatic_rollover_unused_downloads',
    'includes_all_sizes_formats',
  ],
  editorial: [
    'access_news_sports_entertainment_archive_award_winning_photo',
    'millions_creative_images_editorial_use',
    'automatic_rollover_unused_downloads',
    'includes_all_sizes_formats',
  ],
};

const VIEW_DETAILS_TRANSLATION_KEY: Record<SubscriptionTier, string> = {
  creative: 'view_license_details',
  editorial: 'view_included_collections_license_details',
};

const VIEW_DETAILS_MODAL_ID: Record<SubscriptionTier, string> = {
  creative: CREATIVE_LICENSE_DETAILS_MODAL_ID,
  editorial: STANDARD_EDITORIAL_USAGE_MODAL_ID,
};

const VIEW_DETAILS_TRACKING: Record<
  SubscriptionTier,
  (ctaText: string) => void
> = {
  creative: trackClickViewLicenseDetailsCreativeSubs,
  editorial: trackClickViewLicenseDetailsEditorialSubs,
};

const SubscriptionBuyCard = ({
  tier,
  pricePerImage,
  total,
  showSavingsBadge = false,
  isExpanded,
  isSelected,
  onExpand,
  onSelect,
}: SubscriptionBuyCardProps) => {
  const t = useTranslations();
  const { setModal } = useAcquisitionModal() as AcquisitionModalState;

  const handleViewDetailsClick = () => {
    VIEW_DETAILS_TRACKING[tier](VIEW_DETAILS_TRANSLATION_KEY[tier]);
    setModal(VIEW_DETAILS_MODAL_ID[tier]);
  };

  return (
    <ExpandableBuyCard
      title={t(TITLE_TRANSLATION_KEY[tier], { number: MONTHLY_IMAGE_COUNT })}
      subtitle={
        <Typography
          variant={TypographyVariant.body.xsmall}
          component={TypographyComponentEnum.DIV}
          data-testid="subscription-buy-card-subtitle"
        >
          {t(SUBTITLE_TRANSLATION_KEY[tier])}
        </Typography>
      }
      price={pricePerImage}
      priceLabel={t('slash_download')}
      badge={showSavingsBadge ? t('save_up_to_ninety_percent') : undefined}
      isExpanded={isExpanded}
      isSelected={isSelected}
      onExpand={onExpand}
      onSelect={onSelect}
      data-testid="subscription-buy-card"
    >
      <List
        className={styles.benefitList}
        data-testid="subscription-buy-card-benefits"
      >
        {BENEFIT_TRANSLATION_KEYS[tier].map((translationKey) => (
          <ListItem
            key={translationKey}
            className={styles.benefitListItem}
          >
            <CheckRoundedIcon
              className={styles.checkMark}
              size="16px"
            />
            <Typography
              variant={TypographyVariant.body.small}
              component={TypographyComponentEnum.SPAN}
            >
              {t(translationKey)}
            </Typography>
          </ListItem>
        ))}
      </List>

      <Button
        variant={ButtonVariantEnum.LINK}
        size={ButtonSizeEnum.EXTRA_SMALL}
        data-testid="subscription-buy-card-view-details"
        onClick={handleViewDetailsClick}
      >
        <CaretText>{t(VIEW_DETAILS_TRANSLATION_KEY[tier])}</CaretText>
      </Button>

      <div
        className={styles.total}
        data-testid="subscription-buy-card-total"
      >
        <Typography
          variant={TypographyVariant.body.small}
          component={TypographyComponentEnum.SPAN}
        >
          {t('total')}
        </Typography>
        <Typography
          variant={TypographyVariant.body.small}
          component={TypographyComponentEnum.SPAN}
        >
          {`${total}${t('per_month')}`}
        </Typography>
      </div>
    </ExpandableBuyCard>
  );
};

export default SubscriptionBuyCard;
